import React from 'react';
import { DOCS_URL } from '../../../../SideMenu/Menu';
import { StyledBodyText } from '../../../../../../common/Styleguide/Common/Text';
import { CustomSelect } from '../../../../../../common/Styleguide/Common/Select';
import { Option } from '@mui/base';
import { StyledButton } from '../../../../../../common/Styleguide/Common/Button';
import { TableCell, TableRow, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {
  ALL_LOADER_TYPES,
  GameLoaderType,
  gameLoaderTypeToName,
  NON_RECOMMENDED_LOADER_TYPES,
  RECOMMENDED_LOADER_TYPES,
  UNITY_GAME_LOADERS,
} from '@crazygames/game-types/src/game-build/loader.type';

function gameLoaderTypeOptions(isGameJam: boolean, excludedGameLoaders: GameLoaderType[]) {
  const loaders = RECOMMENDED_LOADER_TYPES.filter((loader) => !excludedGameLoaders.includes(loader)).map((key) => {
    return {
      label: gameLoaderTypeToName(key),
      value: key,
    };
  });
  if (isGameJam) {
    return loaders.filter((loader) => loader.value !== 'iframe');
  }
  return loaders;
}

function moreGameLoaderTypeOptions(isGameJam: boolean, excludedGameLoaders: GameLoaderType[]) {
  const loaders = NON_RECOMMENDED_LOADER_TYPES.filter((loader) => !excludedGameLoaders.includes(loader)).map((key) => {
    return {
      label: gameLoaderTypeToName(key),
      value: key,
    };
  });
  if (isGameJam) {
    return loaders.filter((loader) => loader.value !== 'iframe');
  }
  return loaders;
}

interface TypeSelectProps {
  gameLoaderType?: GameLoaderType;
  disabled?: boolean;
  onGameLoaderTypeChange: (newValue: GameLoaderType) => void;
  error?: boolean;
  excludedGameLoaders?: GameLoaderType[];
  typeError?: boolean;
  showOtherEnginesInfo?: boolean;
  isGameJam?: boolean;
  isAdmin?: boolean;
}

const SHOW_MORE_LOADER_TYPE_OPTIONS_KEY = 'showMoreLoaders';

export const TypeSelect: React.FunctionComponent<TypeSelectProps> = ({
  onGameLoaderTypeChange,
  gameLoaderType,
  disabled,
  error = false,
  excludedGameLoaders,
  typeError,
  showOtherEnginesInfo,
  isGameJam = false,
  isAdmin,
}) => {
  const [showMoreLoaderTypeOptions, setShowMoreLoaderTypeOptions] = React.useState(
    gameLoaderType ? NON_RECOMMENDED_LOADER_TYPES.includes(gameLoaderType) : false,
  );

  const toggleShowMoreLoaderTypeOptions = (event: React.MouseEvent) => {
    // Prevent event from propagating to avoid closing the dropdown
    event.stopPropagation();
    setShowMoreLoaderTypeOptions(!showMoreLoaderTypeOptions);
  };

  return (
    <TableRow>
      <TableCell>
        <StyledBodyText variant="h3" sx={{ margin: 0 }}>
          Game engine <span>*</span>
          <Tooltip title="Choose between direct hosting on CrazyGames using our supported engines (recommended) or external hosting (embedded via iframe)">
            <InfoIcon style={{ height: 16, width: 16, marginLeft: 4 }} />
          </Tooltip>
        </StyledBodyText>
      </TableCell>
      <TableCell>
        <CustomSelect
          value={gameLoaderType || ''}
          placeholder="Select"
          error={error}
          disabled={disabled}
          onChange={(event: any, newValue: any) => {
            if (newValue === SHOW_MORE_LOADER_TYPE_OPTIONS_KEY) {
              // We end up in this case if people use the arrow + enter keys to select the "Show more/less options" option
              toggleShowMoreLoaderTypeOptions(event);
              return;
            }
            onGameLoaderTypeChange(newValue);
          }}
          maxHeight={'520px'}
        >
          {isAdmin &&
            ALL_LOADER_TYPES.map((loader) => {
              return (
                <Option key={loader} value={loader}>
                  {gameLoaderTypeToName(loader)}
                </Option>
              );
            })}
          {!isAdmin && (
            <>
              {gameLoaderTypeOptions(isGameJam, excludedGameLoaders || []).map((option) => {
                return (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                );
              })}
              {moreGameLoaderTypeOptions(isGameJam, excludedGameLoaders || []).map((option) => {
                return (
                  <Option
                    key={option.value}
                    value={option.value}
                    style={{
                      display: showMoreLoaderTypeOptions ? 'block' : 'none',
                    }}
                  >
                    {option.label}
                  </Option>
                );
              })}
              <Option value={SHOW_MORE_LOADER_TYPE_OPTIONS_KEY}>
                <StyledButton onClick={toggleShowMoreLoaderTypeOptions}>
                  {showMoreLoaderTypeOptions ? 'Show fewer options' : 'Show more options'}
                </StyledButton>
              </Option>
            </>
          )}
        </CustomSelect>
        {gameLoaderType && UNITY_GAME_LOADERS.includes(gameLoaderType) && (
          <StyledBodyText color="white30" variant="bodyLower">
            These{' '}
            <a href={`${DOCS_URL}/resources/optimization-tips/`} target="_blank" rel="noreferrer">
              Unity WebGL export tips
            </a>{' '}
            may help you optimize your game, thus increasing your revenue.
          </StyledBodyText>
        )}
        {typeError && (
          <StyledBodyText variant="bodyLower2" color="error">
            {`Please select a type`}
          </StyledBodyText>
        )}
        {showOtherEnginesInfo && (
          <StyledBodyText variant="bodyLower2" color="white30">
            If your game engine is not listed, you can upload your game as an HTML5 game.
          </StyledBodyText>
        )}
      </TableCell>
    </TableRow>
  );
};

export default TypeSelect;
